/**
 * Cookie Consent API Client
 * Komunikuje s AWS Lambda endpointem pro logování consentů
 */

const API_ENDPOINT = process.env.REACT_APP_CONSENT_API_URL || 'https://r3rflvh0wb.execute-api.eu-central-1.amazonaws.com/consent';

/**
 * Generuje UUID v4 (s fallbackem pro starší prohlížeče)
 */
function generateUUID() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

/**
 * Vrátí persistentní browserId z localStorage (nebo ho vytvoří)
 * Slouží výhradně pro audit souhlasů – není to tracking cookie
 */
function getOrCreateBrowserId() {
  let browserId = localStorage.getItem('fitelite_browserId');
  if (!browserId) {
    browserId = generateUUID();
    localStorage.setItem('fitelite_browserId', browserId);
  }
  return browserId;
}

/**
 * Odeslání cookie consent na backend
 * @param {boolean} consentGiven - Zda uživatel souhlasil
 * @param {string} consentVersion - Verze consent policy
 * @returns {Promise<Object>} Response z API
 */
export const logConsent = async (consentGiven, consentVersion = 'v1.0') => {
  try {
    const consentData = {
      // Identifikace prohlížeče (audit trail)
      browserId: getOrCreateBrowserId(),

      // Consent rozhodnutí
      consentGiven: consentGiven,
      consentVersion: consentVersion,
      consentTypes: consentGiven
        ? ['marketing', 'analytics', 'retargeting']
        : [],
      consentDate: new Date().toISOString(),

      // Kontext stránky
      pageUrl: window.location.href,
      referrer: document.referrer || 'direct',

      // Informace o prohlížeči / zařízení
      browserLanguage: navigator.language || 'unknown',
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown',
      platform: navigator.userAgentData?.platform || 'unknown',
      cookiesEnabled: navigator.cookieEnabled,
      doNotTrack: navigator.doNotTrack || window.doNotTrack || 'unknown',
    };

    console.log('Odesílám consent na API:', consentData);

    const response = await fetch(API_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(consentData)
    });

    if (!response.ok) {
      throw new Error(`API error: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();
    console.log(
      `%c✅ Consent uložen (server potvrdil)\n` +
      `  DynamoDB id:  ${result.id}\n` +
      `  browserId:    ${result.browserId}\n` +
      `  timestamp:    ${result.timestamp}\n` +
      `  consentGiven: ${consentGiven}`,
      'color: green; font-weight: bold'
    );

    return {
      success: true,
      data: result
    };

  } catch (error) {
    console.error('Chyba při logování consent:', error);

    // Ne-blokující chyba - i když API selže, consent se uloží do localStorage
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Retry logika pro případ selhání API
 * @param {boolean} consentGiven
 * @param {number} maxRetries
 * @returns {Promise<Object>}
 */
export const logConsentWithRetry = async (consentGiven, maxRetries = 3) => {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    const result = await logConsent(consentGiven);

    if (result.success) {
      return result;
    }

    // Pokud není poslední pokus, počkej před dalším pokusem
    if (attempt < maxRetries) {
      await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
    }
  }

  // Po všech pokusech vrátit neúspěšný výsledek
  return {
    success: false,
    error: 'Max retries reached'
  };
};
